import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useFeedback } from './FeedbackContext';
import { FeedbackData, FeedbackFormData } from './types';
import { apiClient } from "@/api/client";
import { useAuthentication } from "@/contexts/AuthenticationContext";

interface FeedbackResponse {
  success: boolean;
  message: string;
}

export const FeedbackDialog = () => {
  const { user } = useAuthentication();
  const { isOpen, contextData, closeFeedback } = useFeedback();

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<FeedbackFormData>({
    defaultValues: {
      message: '',
      allow_contact: false,
    }
  });


  const feedbackMutation = useMutation<FeedbackResponse, Error, FeedbackData>({
    mutationFn: async (data: FeedbackData): Promise<FeedbackResponse> => {
      const response = await apiClient.post<FeedbackResponse>('/feedback/', data);
      return response.data;
    },
    onSuccess: () => {
      toast.message("Feedback sent!", {
        description: "Thank you for your feedback. We'll review it shortly.",
      });
      reset();
      closeFeedback();
    },
    onError: () => {
      toast.error("Failed to send feedback, please try again");
    }
  });

  const onSubmit = async (formData: FeedbackFormData): Promise<void> => {

    const feedbackData: FeedbackData = {
      message: formData.message,
      user_id: user.id,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      context: contextData,
      allow_contact: formData.allow_contact
    };

    feedbackMutation.mutate(feedbackData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeFeedback}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Textarea
              id="message"
              {...register('message', { required: 'Message is required' })}
              placeholder="Tell us what's on your mind..."
              rows={4}
              className="resize-none"
            />
            {errors.message && (
              <p className="text-sm text-red-500">{errors.message.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="allowContact"
              {...register('allow_contact')}
              onCheckedChange={(checked) => setValue('allow_contact', !!checked)}
            />
            <Label htmlFor="allowContact" className="text-sm">
              Can we contact you with any questions?
            </Label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeFeedback}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="thalo"
              type="submit"
              disabled={feedbackMutation.isPending}
              className="flex-1"
            >
              {feedbackMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Feedback'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};